import React, { useState, useEffect, useRef } from 'react';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import AIChefAssistant from './components/AIChefAssistant';
import RecipeExplorer from './components/RecipeExplorer';
import RecipeModal from './components/RecipeModal';
import PantryChef from './components/PantryChef';
import TiffinBoxChef from './components/TiffinBoxChef';
import WeeklyPlanner from './components/WeeklyPlanner';
import CommunityKitchen from './components/CommunityKitchen';
import Footer from './components/Footer';
import { I18N_DATA } from './i18n';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    console.error('UI Error in section:', error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '24px', textAlign: 'center', color: '#ff6b6b', background: 'rgba(255,0,0,0.05)', borderRadius: '12px', margin: '20px' }}>
          <h4>Unable to display this section</h4>
          <p style={{ fontSize: '13px', color: '#999', marginTop: '6px' }}>{String(this.state.error?.message || 'Unexpected error')}</p>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function App() {
  const [lang, setLang] = useState('English');
  const [modalRecipe, setModalRecipe] = useState(null);
  const [modalHeadcount, setModalHeadcount] = useState(4);
  const [modalVariants, setModalVariants] = useState([]);
  const [isFullscreenChat, setIsFullscreenChat] = useState(false);
  const [isFloatingOpen, setIsFloatingOpen] = useState(false);
  const floatingRef = useRef(null);

  const t = { ...I18N_DATA.English, ...(I18N_DATA[lang] || {}) };

  const languages = [
    { code: 'English', label: 'English', native: 'English', flag: '🇬🇧' },
    { code: 'Tamil', label: 'Tamil', native: 'தமிழ்', flag: '🇮🇳' },
    { code: 'Hindi', label: 'Hindi', native: 'हिंदी', flag: '🇮🇳' },
    { code: 'Telugu', label: 'Telugu', native: 'తెలుగు', flag: '🇮🇳' },
    { code: 'Kannada', label: 'Kannada', native: 'ಕನ್ನಡ', flag: '🇮🇳' },
    { code: 'Malayalam', label: 'Malayalam', native: 'മലയാളം', flag: '🇮🇳' },
    { code: 'Arabic', label: 'Arabic', native: 'العربية', flag: '🇸🇦' }
  ];

  // Set html dir for Arabic RTL
  useEffect(() => {
    if (lang === 'Arabic') {
      document.documentElement.setAttribute('dir', 'rtl');
      document.documentElement.setAttribute('lang', 'ar');
    } else {
      document.documentElement.setAttribute('dir', 'ltr');
      document.documentElement.setAttribute('lang', 
        lang === 'Tamil' ? 'ta' : 
        lang === 'Hindi' ? 'hi' : 
        lang === 'Telugu' ? 'te' : 
        lang === 'Kannada' ? 'kn' : 
        lang === 'Malayalam' ? 'ml' : 'en'
      );
    }
  }, [lang]);

  // Close floating dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (floatingRef.current && !floatingRef.current.contains(e.target)) {
        setIsFloatingOpen(false);
      }
    };
    if (isFloatingOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isFloatingOpen]);

  const handleOpenRecipeModal = async (recipeObj, headcount = 4, variants = []) => {
    let resolvedVariants = variants || [];
    let fullRecipe = recipeObj;

    if (!recipeObj.dishes && recipeObj.id) {
      try {
        const res = await fetch(`/api/recipes?q=`);
        const data = await res.json();
        const found = (data.recipes || []).find(r => 
          String(r.id) === String(recipeObj.id) || 
          (recipeObj.meal && r.meal && r.meal.toLowerCase() === recipeObj.meal.toLowerCase())
        );
        if (found) {
          fullRecipe = found;
        }
        if ((!resolvedVariants || resolvedVariants.length === 0) && fullRecipe?.core_dish) {
          resolvedVariants = (data.recipes || []).filter(r => r.core_dish === fullRecipe.core_dish);
        }
      } catch (e) {
        console.error(e);
      }
    } else if ((!resolvedVariants || resolvedVariants.length === 0) && fullRecipe?.core_dish) {
      try {
        const res = await fetch('/api/recipes');
        const data = await res.json();
        resolvedVariants = (data.recipes || []).filter(r => r.core_dish === fullRecipe.core_dish);
      } catch (e) {
        console.error(e);
      }
    }

    setModalRecipe(fullRecipe);
    setModalHeadcount(headcount);
    setModalVariants(resolvedVariants);
  };

  const handleOpenInAssistant = (recipe, headcount) => {
    document.getElementById('aiAssistantSection')?.scrollIntoView({ behavior: 'smooth' });
    const input = document.querySelector('.tg-input-field');
    const sendBtn = document.querySelector('.tg-send-btn');
    if (input && sendBtn) {
      input.value = `/recipe ${recipe.meal}`;
      sendBtn.click();
    }
  };

  const handleLaunchFullscreenChat = () => {
    setIsFullscreenChat(true);
    document.getElementById('aiAssistantSection')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="app-root">
      <Navbar
        lang={lang}
        setLang={setLang}
        t={t}
        onOpenFullscreenChat={handleLaunchFullscreenChat}
      />
      <Hero
        t={t}
        lang={lang}
        setLang={setLang}
        onOpenFullscreenChat={handleLaunchFullscreenChat}
      />
      <ErrorBoundary>
        <AIChefAssistant
          t={t}
          lang={lang}
          onOpenRecipeModal={handleOpenRecipeModal}
          isFullscreen={isFullscreenChat}
          setIsFullscreen={setIsFullscreenChat}
        />
      </ErrorBoundary>
      <ErrorBoundary>
        <RecipeExplorer t={t} lang={lang} onOpenRecipeModal={handleOpenRecipeModal} />
      </ErrorBoundary>
      <ErrorBoundary>
        <TiffinBoxChef t={t} lang={lang} onOpenRecipeModal={handleOpenRecipeModal} />
      </ErrorBoundary>
      <ErrorBoundary>
        <CommunityKitchen t={t} lang={lang} />
      </ErrorBoundary>
      <ErrorBoundary>
        <PantryChef t={t} lang={lang} onOpenRecipeModal={handleOpenRecipeModal} />
      </ErrorBoundary>
      <ErrorBoundary>
        <WeeklyPlanner t={t} lang={lang} />
      </ErrorBoundary>
      <Footer t={t} lang={lang} />

      {/* Floating Side Language Quick Dropdown */}
      <div className="floating-lang-dropdown-wrapper" ref={floatingRef}>
        <button
          type="button"
          className={`floating-side-settings-btn ${isFloatingOpen ? 'active' : ''}`}
          onClick={() => setIsFloatingOpen(!isFloatingOpen)}
          title={lang === 'Tamil' ? 'மொழியை மாற்றவும் (🌐)' : (lang === 'Hindi' ? 'भाषा बदलें (🌐)' : (lang === 'Telugu' ? 'భాషను మార్చండి (🌐)' : (lang === 'Kannada' ? 'ಭಾಷೆಯನ್ನು ಬದಲಿಸಿ (🌐)' : (lang === 'Malayalam' ? 'ഭാഷ മാറ്റുക (🌐)' : (lang === 'Arabic' ? 'تغيير اللغة (🌐)' : 'Change Language (🌐)')))))}
          aria-label="Change Language"
        >
          <span className="floating-gear">🌐</span>
          <span className="floating-lang-indicator">
            {lang === 'Tamil' ? 'தமிழ்' : (lang === 'Hindi' ? 'हिंदी' : (lang === 'Telugu' ? 'తెలుగు' : (lang === 'Kannada' ? 'ಕನ್ನಡ' : (lang === 'Malayalam' ? 'മലയാളം' : (lang === 'Arabic' ? 'العربية' : 'EN')))))}
          </span>
          <span className={`floating-arrow ${isFloatingOpen ? 'open' : ''}`}>▴</span>
        </button>

        {isFloatingOpen && (
          <div className="floating-lang-menu animate-dropdown-up">
            <div className="floating-lang-header">
              <span>🌐 {lang === 'Tamil' ? 'மொழி தேர்வு' : (lang === 'Hindi' ? 'भाषा चुनें' : (lang === 'Telugu' ? 'భాషను ఎంచుకోండి' : (lang === 'Kannada' ? 'ಭಾಷೆ ಆಯ್ಕೆ' : (lang === 'Malayalam' ? 'ഭാഷ തിരഞ്ഞെടുക്കുക' : (lang === 'Arabic' ? 'اختر اللغة' : 'Language')))))}</span>
            </div>
            {languages.map((l) => {
              const isActive = lang === l.code;
              return (
                <button
                  key={l.code}
                  type="button"
                  className={`floating-lang-item ${isActive ? 'active' : ''}`}
                  onClick={() => {
                    setLang(l.code);
                    setIsFloatingOpen(false);
                  }}
                >
                  <span className="lang-item-flag">{l.flag}</span>
                  <span className="lang-item-native">{l.native}</span>
                  {isActive && <span className="lang-item-check">✓</span>}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Recipe Modal with Ingredient Visuals */}
      {modalRecipe && (
        <RecipeModal
          recipe={modalRecipe}
          variants={modalVariants}
          initialHeadcount={modalHeadcount}
          lang={lang}
          setLang={setLang}
          t={t}
          onClose={() => {
            setModalRecipe(null);
            setModalVariants([]);
          }}
          onOpenInBot={handleOpenInAssistant}
        />
      )}
    </div>
  );
}
